import { Button, ConfigProvider, Flex, Form, Typography } from 'antd';
import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { CustomRegistrationFormInput } from '../../../components';
import CustomFormDatePicker from '../../../components/CustomFormDatePicker/CustomFormDatePicker';
import AlertFieldsMandatory from '../../../components/AlertFieldsMandatory/AlertFieldsMandatory';

import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import Icon from '@ant-design/icons';
import { ReactComponent as RemoveBlue } from '../../../assets/icons/remove-blue.svg';
import { API } from '../../../services';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import { INITIAL_TRIP_SCHEDULE_PAYLOAD } from '../../../utilities/constants';
import dayjs from 'dayjs';
import { getErrorMessage } from '../../../utilities/commonFunctions';

const { Title, Text } = Typography;

function toISOStringNoMs(date) {
  const d = dayjs(date);
  // Format with seconds only, no milliseconds, no offset, no Z, local IST time
  return d.format('YYYY-MM-DDTHH:mm:ss').toString() + 'Z';
}

const EmployeeForm = ({
  fieldPrefix,
  onRemove,
  isLoaded,
  pickupLocation,
  dropLocation,
  onPickupMapClick,
  onDropMapClick,
  canRemove,
}) => (
  <div
    style={{
      border: '1px solid #444',
      marginBottom: 24,
      padding: 16,
      position: 'relative',
    }}
  >
    <Flex align="center" justify="space-between">
      <div></div>
      {canRemove && (
        <Icon
          component={RemoveBlue}
          style={{ fontSize: 20, cursor: 'pointer' }}
          onClick={onRemove}
        />
      )}
    </Flex>
    <CustomRegistrationFormInput
      label={<Text strong>Employee ID</Text>}
      placeholder="Enter employee ID"
      name={[fieldPrefix, 'employee_id']}
      rules={[
        {
          required: true,
          message: 'Please enter employee ID',
        },
      ]}
    />
    <Form.Item
      label={<Text strong>Pickup Location (select on map)</Text>}
      style={{ marginBottom: 8 }}
    >
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: 200 }}
          center={pickupLocation || { lat: 12.9716, lng: 77.5946 }}
          zoom={12}
          onClick={onPickupMapClick}
        >
          {pickupLocation && <Marker position={pickupLocation} />}
        </GoogleMap>
      )}
      <Flex gap={8} style={{ marginTop: 8 }}>
        <CustomRegistrationFormInput
          label={<Text>Lat</Text>}
          placeholder="Latitude"
          name={[fieldPrefix, 'pickup_lat']}
          value={pickupLocation ? pickupLocation.lat : ''}
          rules={[
            {
              required: true,
              message: 'Please enter pickup latitude',
            },
          ]}
        />
        <CustomRegistrationFormInput
          label={<Text>Lng</Text>}
          placeholder="Longitude"
          name={[fieldPrefix, 'pickup_lng']}
          value={pickupLocation ? pickupLocation.lng : ''}
          rules={[
            {
              required: true,
              message: 'Please enter pickup longitude',
            },
          ]}
        />
      </Flex>
    </Form.Item>
    <Flex vertical gap={8} style={{ width: '50%' }}>
      <Text strong>Pickup Time (optional)</Text>
      <CustomFormDatePicker
        className=""
        name={[fieldPrefix, 'pickup_time']}
        showTime
        minDate={dayjs(dayjs().format('DD-MM-YYYY'), 'DD-MM-YYYY')}
        maxDate={null}
      />
    </Flex>
    <Form.Item
      label={<Text strong>Drop Location (select on map)</Text>}
      style={{ marginBottom: 8 }}
    >
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: 200 }}
          center={dropLocation || { lat: 12.9716, lng: 77.5946 }}
          zoom={12}
          onClick={onDropMapClick}
        >
          {dropLocation && <Marker position={dropLocation} />}
        </GoogleMap>
      )}
      <Flex gap={8} style={{ marginTop: 8 }}>
        <CustomRegistrationFormInput
          label={<Text>Lat</Text>}
          placeholder="Latitude"
          name={[fieldPrefix, 'drop_lat']}
          value={dropLocation ? dropLocation.lat : ''}
          rules={[
            {
              required: true,
              message: 'Please enter drop latitude',
            },
          ]}
        />
        <CustomRegistrationFormInput
          label={<Text>Lng</Text>}
          placeholder="Longitude"
          name={[fieldPrefix, 'drop_lng']}
          value={dropLocation ? dropLocation.lng : ''}
          rules={[
            {
              required: true,
              message: 'Please enter drop longitude',
            },
          ]}
        />
      </Flex>
    </Form.Item>
    <Flex vertical gap={8} style={{ width: '50%' }}>
      <Text strong>Drop Time (optional)</Text>
      <CustomFormDatePicker
        className=""
        name={[fieldPrefix, 'drop_time']}
        showTime
        minDate={dayjs(dayjs().format('DD-MM-YYYY'), 'DD-MM-YYYY')}
        maxDate={null}
      />
    </Flex>
  </div>
);

EmployeeForm.propTypes = {
  fieldPrefix: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  values: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  isLoaded: PropTypes.bool.isRequired,
  pickupLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  dropLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  onPickupMapClick: PropTypes.func.isRequired,
  onDropMapClick: PropTypes.func.isRequired,
  canRemove: PropTypes.bool.isRequired,
};

function TripScheduling() {
  const [form] = Form.useForm();
  const { addErrorMessage } = useContext(ErrorMessageContext);

  const [employeeForms, setEmployeeForms] = useState([
    { id: 0, values: {}, pickupLocation: null, dropLocation: null },
  ]);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JS_API_KEY || '',
  });

  const handleAddEmployee = () => {
    setEmployeeForms(prev => [
      ...prev,
      { id: prev.length, values: {}, pickupLocation: null, dropLocation: null },
    ]);
  };

  const handleRemoveEmployee = index => {
    setEmployeeForms(prev => prev.filter((_, i) => i !== index));
    const values = form.getFieldsValue();
    const newEmployees = { ...(values.employees || {}) };
    delete newEmployees[index];
    form.setFieldsValue({ employees: newEmployees });
  };

  const getLatLngFromEvent = event => ({
    lat: event.latLng.lat(),
    lng: event.latLng.lng(),
  });

  const updateEmployeeLocation = (index, locationKey, coords) => {
    setEmployeeForms(prev =>
      prev.map((emp, i) =>
        i === index ? { ...emp, [locationKey]: coords } : emp
      )
    );
  };

  const updateFormLocationFields = (index, prefix, coords) => {
    form.setFieldValue(['employees', index, `${prefix}_lat`], coords.lat);
    form.setFieldValue(['employees', index, `${prefix}_lng`], coords.lng);
  };

  const handlePickupMapClick = index => event => {
    const coords = getLatLngFromEvent(event);
    updateEmployeeLocation(index, 'pickupLocation', coords);
    updateFormLocationFields(index, 'pickup', coords);
  };

  const handleDropMapClick = index => event => {
    const coords = getLatLngFromEvent(event);
    updateEmployeeLocation(index, 'dropLocation', coords);
    updateFormLocationFields(index, 'drop', coords);
  };

  const onValuesChange = (_, allValues) => {
    setEmployeeForms(prev =>
      prev.map((emp, idx) => ({
        ...emp,
        values: allValues?.employees?.[idx] || {},
      }))
    );
  };

  const onFinish = async values => {
    try {
      await form.validateFields();
      const employeesPayload = (values.employees || []).map((emp, idx) => ({
        employeeID: emp?.employee_id,
        pickup: {
          coordinates: employeeForms[idx]?.pickupLocation
            ? {
                latitude: employeeForms[idx].pickupLocation.lat.toString(),
                longitude: employeeForms[idx].pickupLocation.lng.toString(),
              }
            : undefined,
          time: emp?.pickup_time ? toISOStringNoMs(emp.pickup_time) : undefined,
        },
        drop: {
          coordinates: employeeForms[idx]?.dropLocation
            ? {
                latitude: employeeForms[idx].dropLocation.lat.toString(),
                longitude: employeeForms[idx].dropLocation.lng.toString(),
              }
            : undefined,
          time: emp?.drop_time ? toISOStringNoMs(emp.drop_time) : undefined,
        },
      }));
      const payload = {
        ...INITIAL_TRIP_SCHEDULE_PAYLOAD,
        clientId: values.client_id,
        tripId: values.trip_id,
        tripStartTime: values.trip_time?.[0]
          ? toISOStringNoMs(values.trip_time[0])
          : undefined,
        tripEndTime: values.trip_time?.[1]
          ? toISOStringNoMs(values.trip_time[1])
          : undefined,
        tripPayload: {
          ...INITIAL_TRIP_SCHEDULE_PAYLOAD.tripPayload,
          clientId: values.client_id,
          tripId: values.trip_id,
          dutyStartTime: values.duty_time?.[0]
            ? toISOStringNoMs(values.duty_time[0])
            : undefined,
          dutyEndTime: values.duty_time?.[1]
            ? toISOStringNoMs(values.duty_time[1])
            : undefined,
          tripStartTime: values.trip_time?.[0]
            ? toISOStringNoMs(values.trip_time[0])
            : undefined,
          tripEndTime: values.trip_time?.[1]
            ? toISOStringNoMs(values.trip_time[1])
            : undefined,
          cabDTO: {
            ...INITIAL_TRIP_SCHEDULE_PAYLOAD.tripPayload.cabDTO,
            registration: values.registration,
            driver: {
              ...INITIAL_TRIP_SCHEDULE_PAYLOAD.tripPayload.cabDTO.driver,
              name:
                values.driver_name ||
                INITIAL_TRIP_SCHEDULE_PAYLOAD.tripPayload.cabDTO.driver.name,
              phoneNumbers: values.driver_phone
                ? [values.driver_phone]
                : INITIAL_TRIP_SCHEDULE_PAYLOAD.tripPayload.cabDTO.driver
                    .phoneNumbers,
            },
          },
          employees: employeesPayload.filter(
            emp =>
              emp.employeeID || emp.pickup.coordinates || emp.drop.coordinates
          ),
        },
      };
      const { status } = await API.manageTrip(payload);
      if (status === 200) {
        form.resetFields();
        addErrorMessage('Trip scheduled successfully', 'success');
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to schedule trip. Please try again later'
        )
      );
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginBottom: 0,
            titleMarginTop: 0,
            fontSize: '1rem',
          },
        },
      }}
    >
      <Flex className="full-width" justify="center">
        <Form
          layout="vertical"
          className="trip-management-form"
          scrollToFirstError
          requiredMark={false}
          form={form}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
        >
          <AlertFieldsMandatory />

          <div
            style={{
              backgroundColor: 'var(--primary-fill-color, #1A1C1D)',
              padding: '32px 100px',
            }}
          >
            <Title
              level={5}
              style={{ fontSize: 24, marginTop: 0, marginBottom: 32 }}
            >
              Trip Details
            </Title>
            <CustomRegistrationFormInput
              label={<Text strong>Client ID</Text>}
              placeholder="Enter client ID"
              name="client_id"
              rules={[{ required: true, message: 'Please enter client ID' }]}
            />
            <CustomRegistrationFormInput
              label={<Text strong>Trip ID</Text>}
              placeholder="Enter trip ID"
              name="trip_id"
              rules={[{ required: true, message: 'Please enter trip ID' }]}
            />
            <CustomRegistrationFormInput
              label={<Text strong>Vehicle Registration</Text>}
              placeholder="e.g.: KA01QAK127"
              name="registration"
              rules={[
                {
                  required: true,
                  message: 'Please enter vehicle registration',
                },
              ]}
            />
            <Flex gap={16} classname="full-width">
              <Flex vertical gap={8} style={{ width: '50%' }}>
                <Text strong>Trip Start Time</Text>
                <CustomFormDatePicker
                  className=""
                  name={['trip_time', 0]}
                  rules={[
                    {
                      required: true,
                      message: 'Please select trip start time',
                    },
                  ]}
                  showTime
                  minDate={dayjs(dayjs().format('DD-MM-YYYY'), 'DD-MM-YYYY')}
                  maxDate={null}
                />
              </Flex>
              <Flex vertical gap={8} style={{ width: '50%' }}>
                <Text strong>Trip End Time</Text>
                <CustomFormDatePicker
                  className=""
                  name={['trip_time', 1]}
                  rules={[
                    { required: true, message: 'Please select trip end time' },
                  ]}
                  showTime
                  minDate={dayjs(dayjs().format('DD-MM-YYYY'), 'DD-MM-YYYY')}
                  maxDate={null}
                />
              </Flex>
            </Flex>
          </div>
          <div
            style={{
              backgroundColor: 'var(--primary-fill-color, #1A1C1D)',
              padding: '32px 100px',
            }}
          >
            <Title
              level={5}
              style={{ fontSize: 24, marginTop: 0, marginBottom: 32 }}
            >
              Driver details
            </Title>
            <Flex gap={16} classname="full-width">
              <Flex vertical gap={8} style={{ width: '50%' }}>
                <Text strong>Duty Start Time</Text>
                <CustomFormDatePicker
                  className=""
                  name={['duty_time', 0]}
                  rules={[
                    {
                      required: true,
                      message: 'Please select duty start time',
                    },
                  ]}
                  showTime
                  minDate={dayjs(dayjs().format('DD-MM-YYYY'), 'DD-MM-YYYY')}
                  maxDate={null}
                />
              </Flex>
              <Flex vertical gap={8} style={{ width: '50%' }}>
                <Text strong>Duty End Time</Text>
                <CustomFormDatePicker
                  className=""
                  name={['duty_time', 1]}
                  rules={[
                    { required: true, message: 'Please select duty end time' },
                  ]}
                  showTime
                  minDate={dayjs(dayjs().format('DD-MM-YYYY'), 'DD-MM-YYYY')}
                  maxDate={null}
                />
              </Flex>
            </Flex>
            <CustomRegistrationFormInput
              label={<Text strong>Driver Name (optional)</Text>}
              placeholder="Enter driver name"
              name="driver_name"
            />
            <CustomRegistrationFormInput
              label={<Text strong>Driver Phone Number (optional)</Text>}
              placeholder="Enter driver phone number"
              name="driver_phone"
            />
          </div>
          <div
            style={{
              backgroundColor: 'var(--primary-fill-color, #1A1C1D)',
              padding: '32px 100px',
            }}
          >
            <Title
              level={5}
              style={{ fontSize: 24, marginTop: 0, marginBottom: 32 }}
            >
              Employees pickup and drop details
            </Title>
            <Form.List name="employees">
              {(fields, { add }) => (
                <>
                  {employeeForms.map((emp, idx) => (
                    <EmployeeForm
                      key={emp.id}
                      fieldPrefix={fields[idx]?.name ?? idx}
                      values={emp.values}
                      onRemove={() => handleRemoveEmployee(idx)}
                      isLoaded={isLoaded}
                      pickupLocation={emp.pickupLocation}
                      dropLocation={emp.dropLocation}
                      onPickupMapClick={handlePickupMapClick(idx)}
                      onDropMapClick={handleDropMapClick(idx)}
                      canRemove={employeeForms.length > 1} // <-- pass canRemove prop
                    />
                  ))}
                  <Button
                    block
                    onClick={() => {
                      add();
                      handleAddEmployee();
                    }}
                  >
                    + Add Employee
                  </Button>
                </>
              )}
            </Form.List>
          </div>
          <Flex gap={24} justify="end">
            <Button
              type="primary"
              onClick={() => form.submit()}
              style={{ width: 188 }}
            >
              Submit
            </Button>
          </Flex>
        </Form>
      </Flex>
    </ConfigProvider>
  );
}

export default TripScheduling;
